import { z } from 'zod'

/**
 * Firebase RTDB never stores null: writing a null-valued key removes it, so a
 * canonical record read straight from RTDB omits every null field. Validate
 * such a record against its canonical contract by restoring exactly the keys
 * the contract declares `.nullable()`. Required fields, unknown keys and wrong
 * types stay as strict as the contract itself.
 */
export function rtdbRecordSchema<Schema extends z.ZodTypeAny>(schema: Schema) {
  return z.preprocess((value) => restoreNullKeys(schema, value), schema)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function restoreNullKeys(schema: z.ZodTypeAny, value: unknown): unknown {
  if (schema instanceof z.ZodEffects) {
    return restoreNullKeys(schema.innerType() as z.ZodTypeAny, value)
  }
  if (schema instanceof z.ZodOptional || schema instanceof z.ZodNullable) {
    return restoreNullKeys(schema.unwrap() as z.ZodTypeAny, value)
  }
  if (schema instanceof z.ZodArray) {
    const element = schema.element as z.ZodTypeAny
    return Array.isArray(value)
      ? value.map((item) => restoreNullKeys(element, item))
      : value
  }
  if (!(schema instanceof z.ZodObject) || !isRecord(value)) return value
  const restored: Record<string, unknown> = { ...value }
  const shape = schema.shape as Record<string, z.ZodTypeAny>
  for (const [key, field] of Object.entries(shape)) {
    if (Object.hasOwn(restored, key)) {
      restored[key] = restoreNullKeys(field, restored[key])
    } else if (field instanceof z.ZodNullable) {
      restored[key] = null
    }
  }
  return restored
}
